import { useEffect, useState } from 'react'
import { Marke } from './Marke.js'
import { api, type Einladungsansicht } from '../net/apiClient.js'
import { fehlerText } from '../net/errors.js'

/**
 * Die Einladungsseite einer Kita-Gemeinschaft.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * Wer hier landet
 * ════════════════════════════════════════════════════════════════════════════
 *
 * Eine Betreiberin hat eine Inhaberin eingeladen. Die Eingeladene klickt den
 * Link aus ihrer Mail und ist in diesem Augenblick nicht angemeldet – oft hat
 * sie noch gar keinen Zugang. Deshalb ist diese Seite öffentlich und zeigt
 * genau zwei Wege:
 *
 *   Neu bei Raporto       → anmelden, Paket wählen, bezahlen. Danach gehört
 *                           ihr Betrieb zur Gemeinschaft.
 *   Schon ein Betrieb     → anmelden; der Beitritt steht dann in ihrem
 *                           eigenen Bereich unter «Kita-Gemeinschaft».
 *
 * ════════════════════════════════════════════════════════════════════════════
 * Warum sie aussieht wie die Anmeldung
 * ════════════════════════════════════════════════════════════════════════════
 *
 * Weil sie dieselbe Art Seite ist: eine Station auf dem Weg hinein, ohne
 * Anmeldung, mit einer Entscheidung darauf. Sie benutzt deshalb denselben
 * Bauplan wie `Login.tsx` – `rp-pg-anmeldung`, die Markenspalte links, die
 * Karte rechts – und keine eigenen Stile.
 *
 * Hier stand vorher `rp-login-karte`. Diese Klasse gibt es in keiner
 * Stilvorlage, und zwar seit jeher: Die Seite rendete als nackter Kasten mit
 * Browserstandard. Wer so bei einer Kita ankommt, auf deren Namen er gleich
 * ein Abonnement abschliessen soll, klickt zu Recht weg.
 *
 * Links steht die Kita, nicht Raporto: ihr Zeichen, ihr Name, ihr Ort. Die
 * Eingeladene wurde von der Kita eingeladen – die Frage, auf die diese Seite
 * antworten muss, ist «von wem?».
 *
 * ════════════════════════════════════════════════════════════════════════════
 * Was diese Seite ausdrücklich nicht tut
 * ════════════════════════════════════════════════════════════════════════════
 *
 * Sie legt nichts an, bucht nichts und kostet nichts. Und sie verrät nichts
 * über die Mandanten: Der Server gibt zu einem Schlüssel den gemeinsamen
 * Briefkopf der Kita und den Namen der Eingeladenen – keine Mitgliederliste,
 * keine Zahl, keine Adresse, UID oder Bankverbindung eines Betriebs.
 *
 * Der zweite Weg braucht den Schlüssel NACH der Anmeldung. Er wird deshalb im
 * Sitzungsspeicher des Browsers abgelegt – nicht im dauerhaften Speicher:
 * Eine Einladung ist ein einmaliger Vorgang und soll nicht wochenlang in
 * einem fremden Browser liegen. Geht er verloren, öffnet sie den Link
 * einfach erneut.
 */

export const EINLADUNG_SPEICHER = 'rp_dach_einladung'


export function Gemeinschaftseinladung({
  token,
  aufBestellen,
  aufLogin,
  aufStart
}: {
  token: string
  aufBestellen: (token: string) => void
  aufLogin: () => void
  aufStart: () => void
}): JSX.Element {
  const [ansicht, setAnsicht] = useState<Einladungsansicht | null>(null)
  const [fehler, setFehler] = useState('')
  /* Ein Logo, das nicht lädt, soll kein leeres Kästchen hinterlassen. */
  const [logoFehlt, setLogoFehlt] = useState(false)

  useEffect(() => {
    let weg = false
    void (async () => {
      try {
        const { einladung } = await api.dachEinladungAnsehen(token)
        if (weg) return
        setAnsicht(einladung)
        try {
          window.sessionStorage.setItem(EINLADUNG_SPEICHER, token)
        } catch {
          /*
            Privates Fenster, gesperrter Speicher: Dann fehlt der Schlüssel
            nach der Anmeldung, und die Eingeladene öffnet den Link noch
            einmal. Kein Grund für eine Fehlermeldung auf dieser Seite.
          */
        }
      } catch (e) {
        if (!weg) setFehler(fehlerText(e))
      }
    })()
    return () => {
      weg = true
    }
  }, [token])

  /* Die Markenspalte – links, in jedem der drei Zustände dieselbe. */
  function spalte(kopf: JSX.Element, punkte: string[]): JSX.Element {
    return (
      <aside className="rp-an-marke">
        <div className="rp-an-knopfzeile">
          <button className="rp-pg-marke" type="button" onClick={aufStart} id="btn-marke">
            <Marke groesse={30} />
          </button>
          <button
            className="rp-pg-knopf rp-pg-knopf-glas rp-pg-knopf-klein"
            type="button"
            id="btn-zurueck-start"
            onClick={aufStart}
          >
            Zurück zur Übersicht
          </button>
        </div>

        {kopf}

        <ul className="rp-an-punkte">
          {punkte.map((p) => (
            <li key={p}>{p}</li>
          ))}
        </ul>
      </aside>
    )
  }

  if (fehler) {
    return (
      <div className="rp-pg rp-pg-anmeldung" id="einladung-fehler">
        <div className="rp-an">
          {spalte(
            <p className="rp-an-satz">Eine Einladung gilt eine Weile, nicht ewig.</p>,
            [
              'Links aus einer Mail gelten vierzehn Tage',
              'Jeder Link gilt einmal',
              'Eine neue Einladung ist jederzeit möglich'
            ]
          )}
          <main className="rp-an-seite">
            <div className="rp-an-karte">
              <span className="rp-strahl" aria-hidden="true">
                <span />
              </span>
              <h1>Diese Einladung gilt nicht mehr</h1>
              <p className="rp-an-unter">{fehler}</p>
              <button
                type="button"
                className="rp-pg-knopf rp-pg-knopf-hell"
                id="einladung-zurueck"
                onClick={aufStart}
              >
                Zur Übersicht
              </button>
              <p className="rp-an-hinweis">
                Bitte die Kita, dir eine neue Einladung zu schicken – das dauert bei ihr
                einen Klick.
              </p>
            </div>
          </main>
        </div>
      </div>
    )
  }

  if (!ansicht) {
    return (
      <div className="rp-pg rp-pg-anmeldung" id="einladung-laedt">
        <div className="rp-an">
          {spalte(<p className="rp-an-satz">Einen Moment …</p>, [])}
          <main className="rp-an-seite">
            <div className="rp-an-karte">
              <span className="rp-strahl" aria-hidden="true">
                <span />
              </span>
              <h1>Einladung wird geöffnet</h1>
              <p className="rp-an-unter">Wir sehen nach, wer dich eingeladen hat.</p>
            </div>
          </main>
        </div>
      </div>
    )
  }

  const ortszeile = [ansicht.zip, ansicht.ort].filter(Boolean).join(' ')

  return (
    <div className="rp-pg rp-pg-anmeldung" id="einladung-seite">
      <div className="rp-an">
        {spalte(
          <div>
            {/*
              Masse inline und keine neue Klasse: Dieselbe Lösung wie im
              Dachbereich, und eine Stilvorlage wächst nicht um eine Zeile
              für ein einziges Bild.
            */}
            {ansicht.hatLogo && !logoFehlt && (
              <img
                id="einladung-logo"
                src={api.dachEinladungLogoUrl(token)}
                alt={`Zeichen von ${ansicht.kita}`}
                onError={() => setLogoFehlt(true)}
                style={{ maxHeight: '72px', maxWidth: '220px', marginBottom: '18px' }}
              />
            )}
            <p className="rp-an-satz" id="einladung-kita">
              {ansicht.kita} lädt dich ein.
            </p>
            {(ansicht.street || ortszeile) && (
              <p className="rp-an-hinweis" id="einladung-adresse">
                {[ansicht.street, ortszeile].filter(Boolean).join(', ')}
              </p>
            )}
          </div>,
          [
            'Dein eigener Bereich, dein eigenes Abo',
            'Gemeinsamer Briefkopf auf jeder Rechnung',
            'Deine Kinder, Eltern und Zahlen bleiben deine'
          ]
        )}

        <main className="rp-an-seite">
          <div className="rp-an-karte">
            <span className="rp-strahl" aria-hidden="true">
              <span />
            </span>

            <h1>Du bist eingeladen</h1>
            <p className="rp-an-unter" id="einladung-gruss">
              Hallo {ansicht.name}, du führst künftig deinen eigenen Bereich bei{' '}
              <strong>{ansicht.kita}</strong>
              {ansicht.ort ? ` in ${ansicht.ort}` : ''}.
            </p>

            <ul className="rp-an-punkte" id="einladung-was">
              <li>
                Eure Rechnungen tragen denselben Kita-Namen, dasselbe Logo und dieselbe
                Adresse.
              </li>
              <li>
                Deine Kinder, Eltern, Rechnungen und deine IBAN bleiben deine. Niemand
                sonst sieht sie – auch die Kita-Verwaltung nicht.
              </li>
              <li>
                Du wählst dein Abo selbst, ab CHF 19.90 im Monat, und verwaltest es
                selbst.
              </li>
            </ul>

            <button
              type="button"
              className="rp-pg-knopf rp-pg-knopf-hell"
              id="einladung-neu"
              onClick={() => aufBestellen(token)}
            >
              Ich bin neu bei Raporto
            </button>

            <button
              type="button"
              className="rp-pg-knopf rp-pg-knopf-glas"
              id="einladung-bestehend"
              onClick={aufLogin}
            >
              Ich habe schon einen Raporto-Betrieb
            </button>

            <p className="rp-an-hinweis" id="einladung-hinweis">
              Hast du schon einen Betrieb bei Raporto? Melde dich an – der Beitritt steht
              danach in deinem Bereich unter «Kita-Gemeinschaft». Deine bestehenden
              Kunden, Rechnungen und Zahlungen bleiben dabei unberührt; es entsteht kein
              zweiter Betrieb und kein zweites Abo.
            </p>
          </div>
        </main>
      </div>
    </div>
  )
}
